import { useState } from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { Plus, Loader2, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useQuotationTemplates, useDeleteTemplate } from "@/features/quotation-templates/api";
import { ActionGuard } from "@/components/auth/ActionGuard";
import { useModuleAccess } from "@/hooks/useIsReadOnly";
import { useBranch } from "@/context/BranchContext";
import { IconButton } from "@/components/ui/icon-button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const Route = createFileRoute("/_authenticated/quotation-templates/")({
  component: QuotationTemplatesIndex,
});

function QuotationTemplatesIndex() {
  const [templateToDelete, setTemplateToDelete] = useState<{ id: string; name: string } | null>(null);
  const navigate = useNavigate();
  const { currentBranchId, lockedBranchId } = useBranch();
  const effectiveBranchId = lockedBranchId ?? currentBranchId ?? null;
  const { data: data = [], isLoading } = useQuotationTemplates(effectiveBranchId);
  const { canWrite } = useModuleAccess("quotation_templates");
  const del = useDeleteTemplate();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Quotation Templates</h1>
          <p className="text-sm text-muted-foreground">Reusable RFQ templates with custom fields, terms &amp; validity.</p>
        </div>
        <ActionGuard can={canWrite}>
          <Button onClick={() => navigate({ to: "/quotation-templates/new" })}><Plus className="h-4 w-4 mr-1" />New Template</Button>
        </ActionGuard>
      </div>
      <Card><CardContent className="p-0">
        {isLoading ? <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div> :
          data.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">No templates yet. Create your first template to standardise RFQs.</div> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Validity (days)</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.map((t) => (
                <TableRow key={t.id} className="cursor-pointer" onClick={() => navigate({ to: "/quotation-templates/$templateId", params: { templateId: t.id } })}>
                  <TableCell className="font-medium">{t.name}{t.is_default && <Badge className="ml-2 bg-emerald-600">Default</Badge>}</TableCell>
                  <TableCell className="text-muted-foreground">{t.description ?? "—"}</TableCell>
                  <TableCell>{t.validity_days ?? "—"}</TableCell>
                  <TableCell>{t.is_active ? <Badge>Active</Badge> : <Badge variant="secondary">Inactive</Badge>}</TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <ActionGuard can={canWrite}>
                      <IconButton asChild size="sm" label={`Edit quotation template ${t.name}`} tooltip="Edit quotation template">
                        <Link to="/quotation-templates/$templateId" params={{ templateId: t.id }}>
                          <Edit className="h-4 w-4" />
                        </Link>
                      </IconButton>
                      <IconButton size="sm" label={`Delete quotation template ${t.name}`} tooltip="Delete quotation template"
                        onClick={() => setTemplateToDelete({ id: t.id, name: t.name })}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </IconButton>
                    </ActionGuard>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>

      <AlertDialog open={!!templateToDelete} onOpenChange={(open) => !open && setTemplateToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the template "{templateToDelete?.name}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                if (!templateToDelete) return;
                try {
                  await del.mutateAsync(templateToDelete.id);
                  toast.success("Deleted");
                } catch (e) {
                  toast.error((e as Error).message);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
